import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchDailyStatus, startMorningChain as startMorningChainApi } from '../api/dailyApi';
import { useStorage } from '../contexts/StorageContext';
import type { DailyStatus } from '../types/daily';
import { appMessage as message } from '../utils/appMessage';

/**
 * ① '오늘 받기 완료' · 아침 서버 체인(감사 card-controltower#566, 백엔드 PR #78 Q1).
 *
 * - '오늘 받기 완료' 는 localStorage 대신 서버 `GET /daily-status` 의 updateDone — 다른 PC · 직원 · 07:30 서버 체인도 같은 값.
 *   (로컬 표시와 OR — 옛 잡 API 로 받은 것도 그대로 완료로.)
 * - ① 은 `POST /morning-chain`(동기화 → 가격 받기 → 시세 분류 → 상승분 자동 반영, 서버가 이어서) — 예전 브라우저 사슬
 *   (동기화 끝을 기다려 가격 받기, 가격 받기가 끝나면 분류)은 탭을 닫으면 끊겼다. 옛 서버(404)면 'unsupported' — 부른 쪽이 예전 사슬로.
 * - 체인이 도는 동안(running) 단계 잡(jobId)을 카드 진행바에 붙여 지금처럼 진행 · 결과(가격 받기 결과 병합 등)를 받고, 끝나면 onChainFinished.
 *   체인의 잡은 isServerChainJob 으로 알아본다 — 가격 받기 뒤 브라우저가 분류를 또 시작하지 않게(CardPageContext).
 */

const chainJobIds = new Set<string>();
/** 아침 서버 체인의 단계 잡인가 — 분류 · 상승분 반영은 서버가 이어서 하므로 브라우저 사슬을 걸지 않는다. */
export const isServerChainJob = (jobId: string | null | undefined): boolean => !!jobId && chainJobIds.has(jobId);
/** 테스트 전용. */
export const resetServerChainJobsForTest = (): void => { chainJobIds.clear(); };

const POLL_MS = 4_000;
/** 체인을 시작한 뒤 running 이 한 번도 안 보여도 기다림을 푸는 상한(감사 #657). */
const EXPECT_MAX_MS = 120_000;

export type StartChainResult = 'started' | 'running' | 'unsupported' | 'failed';

export function useServerDaily(opts: {
  activeJobId: string | null;
  setActiveJobId: (jobId: string) => void;
  /** 체인이 끝났을 때(성공 · 실패 · 중단) — 카드 · 검토 목록을 다시 받는다. */
  onChainFinished: () => void;
}) {
  const { activeJobId, setActiveJobId, onChainFinished } = opts;
  const { store, superStore, isLoggedIn } = useStorage();
  const scope = `${isLoggedIn ? 'in' : 'out'}:${superStore ?? store ?? 'none'}`;
  const [daily, setDaily] = useState<DailyStatus | null>(null);
  // null = 아직 모름, false = 옛 서버(404)
  const [supported, setSupported] = useState<boolean | null>(null);
  // 체인을 막 시작했다 — 첫 daily-status 에 running 이 아직 안 보여도 폴링한다.
  const [expecting, setExpecting] = useState(false);
  const sawRunningRef = useRef(false);
  // 이 화면이 시작한 체인(POST 응답의 chainId) — running 을 보기 전에 끝나거나 실패해도 chains[] 에서 끝을 알아본다(감사 #657).
  const expectChainIdRef = useRef<string | null>(null);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const finishedRef = useRef(onChainFinished);
  finishedRef.current = onChainFinished;

  const refresh = useCallback(async (): Promise<DailyStatus | null> => {
    const at = scopeRef.current;
    try {
      const d = await fetchDailyStatus();
      if (at !== scopeRef.current) return null; // 매장 · 계정이 바뀐 뒤 늦게 온 응답
      if (!d) { setSupported(false); setDaily(null); return null; }
      setSupported(true);
      setDaily(d);
      return d;
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (at === scopeRef.current && status === 404) { setSupported(false); setDaily(null); }
      return null;
    }
  }, []);

  // 매장 · 계정이 바뀌면 처음부터.
  useEffect(() => {
    setDaily(null);
    setSupported(null);
    setExpecting(false);
    sawRunningRef.current = false;
    expectChainIdRef.current = null;
    if (scope.startsWith('in:') && !scope.endsWith(':none')) void refresh();
  }, [scope, refresh]);

  const running = daily?.running ?? null;

  // 도는 동안(또는 막 시작한 뒤) 폴링.
  useEffect(() => {
    if (!running && !expecting) return undefined;
    const id = window.setInterval(() => { void refresh(); }, POLL_MS);
    return () => window.clearInterval(id);
  }, [running, expecting, refresh]);

  // running 이 보이면 기다림을 끝내고, running → 없음 이면 체인이 끝난 것.
  useEffect(() => {
    if (running) {
      sawRunningRef.current = true;
      expectChainIdRef.current = null;
      if (expecting) setExpecting(false);
      return;
    }
    if (sawRunningRef.current) {
      sawRunningRef.current = false;
      finishedRef.current();
    }
  }, [running, expecting]);

  // 시작한 체인이 running 을 보기 전에 끝났다(빨리 끝남 · 바로 실패) — chains[] 에 그 체인이 RUNNING 이 아니면 기다림을 푼다(감사 #657).
  // 예전엔 running 을 한 번 본 뒤에만 내려가 ① '조회 중...' · ③ 이 매장을 바꿀 때까지 잠겼다.
  useEffect(() => {
    if (!expecting || running || !daily) return;
    const id = expectChainIdRef.current;
    if (!id) return;
    const c = daily.chains.find((x) => x.chainId === id);
    if (c && c.status !== 'RUNNING') {
      expectChainIdRef.current = null;
      setExpecting(false);
      finishedRef.current();
    }
  }, [expecting, running, daily]);

  // chainId 를 모를 때(409 로 따라감 · 옛 응답)도 영원히 기다리지 않는다 — 상한 뒤 기다림만 푼다(폴링 · 진행 표시가 멈춘다).
  useEffect(() => {
    if (!expecting) return undefined;
    const id = window.setTimeout(() => { expectChainIdRef.current = null; setExpecting(false); }, EXPECT_MAX_MS);
    return () => window.clearTimeout(id);
  }, [expecting]);

  // 단계 잡을 카드 진행바에 붙인다 — 다른 잡이 진행바를 쓰고 있으면 끝날 때까지 기다린다.
  const runningJobId = running?.jobId ?? null;
  useEffect(() => {
    if (!runningJobId || activeJobId || chainJobIds.has(runningJobId)) return;
    chainJobIds.add(runningJobId);
    setActiveJobId(runningJobId);
  }, [runningJobId, activeJobId, setActiveJobId]);

  // 단계 잡이 끝나면(진행바가 비면) 다음 단계를 바로 본다.
  const prevActiveRef = useRef(activeJobId);
  useEffect(() => {
    const prev = prevActiveRef.current;
    prevActiveRef.current = activeJobId;
    if (prev && !activeJobId && (chainJobIds.has(prev) || sawRunningRef.current)) void refresh();
  }, [activeJobId, refresh]);

  const startMorningChain = useCallback(async (): Promise<StartChainResult> => {
    if (supported === false) return 'unsupported';
    const at = scopeRef.current;
    try {
      const { chainId } = await startMorningChainApi();
      // 응답 전에 매장 · 계정이 바뀌었다 — 시작은 그 매장의 일이니 이 화면(새 매장)의 기다림 · 폴링을 켜지 않는다(감사 #657).
      if (at !== scopeRef.current) return 'started';
      expectChainIdRef.current = chainId;
      message.info({
        key: 'morning-chain',
        content: '가격 받기를 시작했습니다 — 서버가 동기화 → 가격 받기 → 시세 분류 → 상승분 자동 반영을 이어서 합니다(창을 닫아도 계속).',
        duration: 5,
      });
      setExpecting(true);
      void refresh();
      return 'started';
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: string } } };
      const status = err?.response?.status;
      if (status === 404) { setSupported(false); return 'unsupported'; }
      if (at !== scopeRef.current) return 'failed';
      if (status === 409) {
        // 409 는 체인 진행 중만이 아니다 — 실패 카드 다시 돌리기(백엔드 PR #95, trigger RERUN)가 도는 중에도 온다. 상태를 한 번 받아
        // **도는 체인(running)이 보일 때만** 따라간다. 안 보이면(재실행 중 · 그 사이 끝남 · 상태 조회 실패) 따라갈 체인이 없는데
        // expecting 을 켜면 ① 이 최대 2분 '받는 중' 으로 잠겼다(코덱 PR #94 중간, 감사 card-controltower#811) — 문구만 띄우고 끝낸다.
        const d = await refresh();
        if (at !== scopeRef.current) return 'failed';
        if (!d?.running) {
          const rerunning = !!d && d.chains.some((c) => c.trigger === 'RERUN' && c.status === 'RUNNING');
          message.warning({
            key: 'morning-chain',
            content: err.response?.data?.message
              || (rerunning ? '실패 카드 다시 돌리기가 진행 중입니다. 끝난 뒤 다시 시도해 주세요.' : '다른 작업이 진행 중입니다. 잠시 뒤 다시 시도해 주세요.'),
            duration: 6,
          });
          return 'failed';
        }
        expectChainIdRef.current = null;
        message.warning({ key: 'morning-chain', content: err.response?.data?.message || '아침 가격 받기가 이미 진행 중입니다.', duration: 5 });
        // 도는 체인이 보였다 — running 폴링이 따라간다(expecting 은 running 을 보면 바로 내려간다).
        setExpecting(true);
        return 'running';
      }
      message.error({ key: 'morning-chain', content: err?.response?.data?.message || '가격 받기 시작 중 오류가 발생했습니다.', duration: 6 });
      return 'failed';
    }
  }, [supported, refresh]);

  return {
    /** 서버가 아는 오늘 상태(옛 서버 · 모름이면 null). */
    daily,
    supported,
    /** 서버 기준 오늘 ① 완료(모르면 false — 로컬 표시와 OR 해서 쓴다). */
    serverUpdateDone: daily?.updateDone === true,
    /** 체인이 도는 중(막 시작해 아직 안 보이는 때 포함). */
    chainRunning: !!running || expecting,
    startMorningChain,
    refreshDaily: refresh,
  };
}
